import { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { Maximize2, Loader2 } from "lucide-react";
import Lightbox from "@/components/Lightbox";

// Single-page magazine viewer for decks whose PDF pages are self-contained
// (landscape) pages. Shows ONE page at a time, large and readable, with a
// subtle turn animation, prev/next, a counter, swipe + keyboard, and
// click-to-zoom (full-screen Lightbox). A fixed aspect-ratio frame keeps the
// layout stable so the page never collapses while a new image loads.
export default function MagazineDeck({ pages = [], title = "Magazine" }) {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [zoom, setZoom] = useState(-1);
  const [ratio, setRatio] = useState(2200 / 1677); // width / height
  const [loading, setLoading] = useState(true);
  const touch = useRef(null);
  const controls = useAnimationControls();
  const total = pages.length;

  const go = (delta) =>
    setIndex((i) => {
      setDir(delta >= 0 ? 1 : -1);
      return Math.min(total - 1, Math.max(0, i + delta));
    });

  // measure the real page aspect ratio from the first page
  useEffect(() => {
    if (!pages[0]) return;
    const im = new Image();
    im.onload = () => im.naturalHeight && setRatio(im.naturalWidth / im.naturalHeight);
    im.src = pages[0];
  }, [pages]);

  // play a quick turn animation whenever the page changes (image element is
  // persistent, only its src swaps, so navigation never cancels the fetch).
  useEffect(() => {
    setLoading(true);
    controls.set({ x: dir > 0 ? 38 : -38, opacity: 0.35 });
    controls.start({ x: 0, opacity: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } });
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // preload neighbours for instant turns
  useEffect(() => {
    [index + 1, index - 1, index + 2].forEach((i) => {
      if (i >= 0 && i < total) {
        const im = new Image();
        im.src = pages[i];
      }
    });
  }, [index, total, pages]);

  useEffect(() => {
    const onKey = (e) => {
      if (zoom >= 0) return; // lightbox handles its own keys
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, total]);

  if (!total) return null;

  return (
    <div
      className="flipbook-stage relative flex flex-col items-center gap-6 rounded-2xl py-8 md:py-10 px-3 md:px-6"
      data-lenis-prevent data-testid="magazine-deck"
    >
      <button
        onClick={() => setZoom(index)}
        data-cursor="hover" data-testid="deck-zoom"
        className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 rounded-full border border-border bg-bg/70 backdrop-blur px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink hover:border-lime hover:text-lime transition-colors"
        aria-label="View full screen"
      >
        <Maximize2 size={13} /> <span className="hidden sm:inline">Full screen</span>
      </button>

      <button
        onClick={() => setZoom(index)}
        data-cursor="hover" data-cursor-text="Zoom" data-testid="deck-page"
        className="relative w-full max-w-[1120px] overflow-hidden rounded-lg bg-surface shadow-2xl"
        style={{ aspectRatio: String(ratio) }}
        onTouchStart={(e) => (touch.current = e.changedTouches[0].clientX)}
        onTouchEnd={(e) => {
          if (touch.current == null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          touch.current = null;
        }}
      >
        <motion.img
          animate={controls}
          src={pages[index]}
          alt={`${title} page ${index + 1}`}
          onLoad={() => setLoading(false)}
          className="absolute inset-0 w-full h-full object-contain select-none"
          draggable={false}
        />
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25" data-testid="deck-loading">
            <Loader2 size={26} className="animate-spin text-lime" />
          </span>
        )}
      </button>

      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest">
        <button onClick={() => go(-1)} disabled={index === 0} data-cursor="hover" data-testid="deck-prev"
          className="rounded-full border border-border px-4 py-2 text-ink hover:border-lime hover:text-lime disabled:opacity-30 transition-colors">‹ Prev</button>
        <span className="tabular-nums text-muted2 min-w-[64px] text-center" data-testid="deck-counter">
          {index + 1} / {total}
        </span>
        <button onClick={() => go(1)} disabled={index === total - 1} data-cursor="hover" data-testid="deck-next"
          className="rounded-full border border-border px-4 py-2 text-ink hover:border-lime hover:text-lime disabled:opacity-30 transition-colors">Next ›</button>
      </div>

      <Lightbox
        items={pages.map((u, i) => ({ url: u, type: "image", caption: `${title}, page ${i + 1}` }))}
        index={zoom}
        onClose={() => setZoom(-1)}
        onIndex={setZoom}
      />
    </div>
  );
}
